import { useState, useEffect, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, FileText, Download, X, Info, Sparkles } from "lucide-react";
import { downloadInvoicePdf, DEFAULT_CLOSING_NOTE } from "../utils/invoicePdf";
import { downloadErspanisPdf, hasErspanis } from "../utils/schuldenPdf";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InfoModal from "../components/InfoModal";
import DownloadWarningModal from "../components/DownloadWarningModal";
import { userStorage } from '../utils/userStorage';
import {
  MARKUP_MEMORY_KEY,
  loadMarkupMemory,
  saveMarkupForCategory,
  markupForCategory,
  groupInvoicesByQuarter,
} from '../utils/invoiceHelpers';

export default function Rechnungen() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [schuldenInvoiceNumbers, setSchuldenInvoiceNumbers] = useState(new Set());
  const [companySettings, setCompanySettings] = useState({});
  // Nach Rechnung-Erstellung: Modal fragen was tun
  const [showPostCreateModal, setShowPostCreateModal] = useState(false);
  const [postCreateInvoice, setPostCreateInvoice] = useState(null);
  // Format-Wahl-Modal (DOCX / PDF)
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [formatModalInvoice, setFormatModalInvoice] = useState(null);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    customer_name: "",
    customer_street: "",
    customer_postal_code: "",
    customer_city: "",
    items: []
  });
  // Zahlung / Abschluss
  const [includeBankDetails, setIncludeBankDetails] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [closingNote, setClosingNote] = useState(DEFAULT_CLOSING_NOTE);
  const [currentItem, setCurrentItem] = useState({
    article: "",
    quantity: "1",
    price_category: "tp",
    markup_percent: markupForCategory("tp", "15"),
    ust_percent: "19"
  });

  useEffect(() => {
    loadInvoices();
    loadProducts();
    loadStock();
    loadContacts();
    loadSchuldenInvoiceNumbers();
    loadCompanySettings();
  }, []);

  const loadCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCompanySettings(response.data || {});
    } catch (error) {
      setCompanySettings({});
    }
  };

  const loadSchuldenInvoiceNumbers = async () => {
    try {
      const response = await axios.get(`${API}/schulden`);
      const nums = new Set(
        (response.data.schulden || [])
          .map(s => s.gruppe_name || s.rechnung_nummer)
          .filter(Boolean)
      );
      setSchuldenInvoiceNumbers(nums);
    } catch (error) {
      console.error("Fehler beim Laden der Schulden-Rechnungen:", error);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${API}/teampartner`);
      console.log("Loaded contacts:", response.data?.length, response.data);
      setContacts(response.data || []);
    } catch (error) {
      console.error("Fehler beim Laden der Kontakte:", error);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Rechnungen");
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Produkte");
    }
  };

  const loadStock = async () => {
    try {
      // Only load geschäftlich stock (invoices are always business-related)
      const response = await axios.get(`${API}/stock?type=${encodeURIComponent('geschäftlich')}`);
      setStock(response.data);
    } catch (error) {
      console.error("Fehler beim Laden des Bestands");
    }
  };

  const handleProductSelect = (productName) => {
    const product = products.find(p => p.name === productName);
    setCurrentItem({
      ...currentItem,
      article: productName,
      ust_percent: product ? product.ust_percent.toString() : "19"
    });
  };

  const calculatePriceBrutto = () => {
    if (!currentItem.article) return 0;
    
    const product = products.find(p => p.name === currentItem.article);
    if (!product) return 0;
    
    // Get price from selected category
    const basePrice = product[currentItem.price_category] || 0;
    if (basePrice === 0) return 0;
    
    const markup = parseFloat(currentItem.markup_percent) || 0;
    return basePrice * (1 + markup / 100);
  };

  const addItem = () => {
    if (!currentItem.article || !currentItem.quantity) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }

    const product = products.find(p => p.name === currentItem.article);
    if (!product) {
      toast.error("Produkt nicht gefunden");
      return;
    }

    // Check stock availability - berücksichtige bereits hinzugefügte Items
    const stockItem = stock.find(s => s.article === currentItem.article);
    const alreadyUsed = formData.items
      .filter(item => item.article === currentItem.article)
      .reduce((sum, item) => sum + item.quantity, 0);
    const availableStock = (stockItem?.stock || 0) - alreadyUsed;
    
    if (availableStock < parseInt(currentItem.quantity)) {
      toast.error(`Nicht genügend Bestand verfügbar. Noch verfügbar: ${availableStock}`);
      return;
    }

    const basePrice = product[currentItem.price_category] || 0;
    if (basePrice === 0) {
      toast.error("Kein Preis für gewählte Kategorie verfügbar");
      return;
    }

    const priceBrutto = calculatePriceBrutto();
    const ustPercent = parseFloat(currentItem.ust_percent);
    const itemBruttoTotal = parseInt(currentItem.quantity) * priceBrutto;
    
    // Rückrechnung: Brutto → Netto
    const itemNetto = itemBruttoTotal / (1 + ustPercent / 100);
    const itemUst = itemBruttoTotal - itemNetto;

    setFormData({
      ...formData,
      items: [...formData.items, {
        article: currentItem.article,
        quantity: parseInt(currentItem.quantity),
        price_category: currentItem.price_category,
        base_price: basePrice,
        markup_percent: parseFloat(currentItem.markup_percent),
        price_brutto: priceBrutto,
        ust_percent: ustPercent,
        item_brutto_total: itemBruttoTotal,
        item_netto: itemNetto,
        item_ust: itemUst
      }]
    });

    // Aufschlag je Preiskategorie merken (für nächstes Artikel-Hinzufügen)
    saveMarkupForCategory(currentItem.price_category, parseFloat(currentItem.markup_percent));

    const nextCat = currentItem.price_category;
    setCurrentItem({
      article: "",
      quantity: "1",
      price_category: nextCat,
      markup_percent: markupForCategory(nextCat, "15"),
      ust_percent: "19"
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    let totalBrutto = 0;
    let totalNetto = 0;
    let totalUst = 0;
    
    formData.items.forEach(item => {
      totalBrutto += item.item_brutto_total || 0;
      totalNetto += item.item_netto || 0;
      totalUst += item.item_ust || 0;
    });
    
    return {
      brutto: totalBrutto,
      netto: totalNetto,
      ust: totalUst
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.items.length === 0) {
      toast.error("Bitte mindestens einen Artikel hinzufügen");
      return;
    }

    const totals = calculateTotal();
    
    // Kundendaten nur Pflicht bei Betrag > 250€
    if (totals.brutto > 250) {
      if (!formData.customer_name || !formData.customer_street || 
          !formData.customer_postal_code || !formData.customer_city) {
        toast.error("Bei Beträgen über 250€ sind vollständige Kundendaten erforderlich");
        return;
      }
      // Hinweis, wenn Steuernummer/USt-IdNr. fehlt
      const taxOrVat = companySettings.ust_id || companySettings.tax_id || companySettings.vat_id;
      if (!taxOrVat) {
        toast.warning(
          "Bei Rechnungen über 250 € ist eine Steuernummer oder USt-IdNr. Pflicht. " +
          "Bitte in Einstellungen hinterlegen."
        );
      }
    }

    if (includeBankDetails && !dueDate) {
      toast.error("Bitte Fälligkeitsdatum angeben, wenn Bankdaten auf die Rechnung sollen.");
      return;
    }

    // Fehler: Bankdaten-Checkbox aktiv, aber keine Bankdaten in Einstellungen
    if (includeBankDetails && !companySettings.iban && !companySettings.bank_name) {
      toast.error(
        "Keine Bankdaten hinterlegt. Bitte IBAN/Bankname in den Einstellungen eintragen.",
        { duration: 6000 }
      );
      return;
    }

    try {
      // Prepare data for backend
      const invoiceData = {
        date: formData.date,
        customer_name: formData.customer_name || "",
        customer_street: formData.customer_street || "",
        customer_postal_code: formData.customer_postal_code || "",
        customer_city: formData.customer_city || "",
        items: formData.items.map(item => ({
          article: item.article,
          quantity: item.quantity,
          price_category: item.price_category,
          markup_percent: item.markup_percent,
          ust_percent: item.ust_percent
        })),
        include_bank_details: includeBankDetails,
        due_date: includeBankDetails ? (dueDate || null) : null,
        // Nur speichern, wenn der User den Default tatsächlich geändert hat.
        // So greift bei leerem oder unverändertem Feld immer der aktuelle Default-Satz.
        closing_note: (() => {
          const trimmed = (closingNote || "").trim();
          if (!trimmed) return null;
          if (trimmed === DEFAULT_CLOSING_NOTE.trim()) return null;
          return trimmed;
        })(),
      };

      const response = await axios.post(`${API}/invoices`, invoiceData);
      const createdInvoice = response.data;
      toast.success(`Rechnung ${createdInvoice.invoice_number} erstellt`);

      setFormData({
        date: new Date().toISOString().split('T')[0],
        customer_name: "",
        customer_street: "",
        customer_postal_code: "",
        customer_city: "",
        items: []
      });
      setIncludeBankDetails(false);
      setDueDate("");
      setClosingNote(DEFAULT_CLOSING_NOTE);
      setShowForm(false);
      loadInvoices();
      loadStock();
      loadSchuldenInvoiceNumbers();

      // Post-Create-Modal öffnen (Download-Format + Als Schuld übernehmen?)
      setPostCreateInvoice(createdInvoice);
      setShowPostCreateModal(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fehler beim Erstellen der Rechnung";
      toast.error(errorMsg);
    }
  };

  const requestDownload = (invoiceId, invoiceNumber) => {
    // Format-Wahl-Modal öffnen
    const invoice = invoices.find(i => i.id === invoiceId);
    if (!invoice) return;
    setFormatModalInvoice(invoice);
    setShowFormatModal(true);
  };

  const downloadAsDocx = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoice.id}/download`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rechnung als DOCX heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  const downloadAsPdf = (invoice) => {
    try {
      downloadInvoicePdf({ invoice, settings: companySettings });
      toast.success("Rechnung als PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim PDF-Erstellen");
    }
  };

  // Baut aus Invoice-Items das Posten-Format, das schuldenPdf erwartet
  const invoiceToErspanisPosten = (invoice) => {
    if (!invoice?.items) return [];
    return invoice.items.map(it => ({
      produkt_name: it.article,
      beschreibung: it.article,
      menge: it.quantity,
      betrag: (it.quantity || 0) * (it.price_brutto || 0),
      kunde_preis: it.kunde_preis,
      price_category: it.price_category,
      gruppe_name: invoice.invoice_number,
      rechnung_nummer: invoice.invoice_number,
    }));
  };

  const downloadErsparnisFromInvoice = (invoice) => {
    try {
      const posten = invoiceToErspanisPosten(invoice);
      const ok = downloadErspanisPdf({
        kundenName: invoice.customer_name || "Kunde",
        posten,
        datum: invoice.date,
        firmenName: companySettings.company_name || "",
      });
      if (ok) toast.success("Ersparnis-PDF heruntergeladen");
      else toast.info("Keine Ersparnis vorhanden (kein Kundenpreis oder kein Vorteil).");
    } catch (error) {
      toast.error("Fehler beim Erstellen des Ersparnis-PDF");
    }
  };

  const confirmDownload = async () => {
    // alt - wird nicht mehr benötigt, aber für Warn-Modal-Callback behalten
    if (!pendingDownload) return;
    setShowDownloadWarning(false);
    setPendingDownload(null);
  };

  const addAsSchuld = (invoice) => {
    // Rechnung-Daten für Schulden-Modal vorbereiten
    const schuldenData = {
      fromInvoice: true,
      rechnung_id: invoice.id,
      rechnung_nummer: invoice.invoice_number,
      customer_name: invoice.customer_name,
      betrag: invoice.total_brutto,
      rechnungs_datum: invoice.date
    };
    
    // In localStorage speichern
    userStorage.setItem('pendingSchuldFromInvoice', JSON.stringify(schuldenData));
    
    // Zur Kunden-Seite navigieren
    navigate('/kunden');
    toast.success("Zur Kunden-Seite weitergeleitet. Bitte Schulden-Tab öffnen.");
  };

  const deleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Rechnung ${invoiceNumber} wirklich löschen?`)) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Rechnung gelöscht");
      loadInvoices();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-800 flex items-center">
                  <FileText className="w-8 h-8 mr-3 text-emerald-600" />
                  Rechnungen
                </h1>
                <p className="text-slate-500 mt-1">Rechnung erstellen und verwalten</p>
              </div>
              <Button 
                onClick={() => setShowInfoModal(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200"
              >
                <Info className="w-4 h-4 mr-2" />
                Rechnungsarten
              </Button>
            </div>
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Rechnung
            </Button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="text-lg font-semibold mb-4">Neue Rechnung erstellen</h3>
              
              <div className="mb-6">
                <h4 className="font-semibold text-slate-700 mb-3">
                  Kundendaten
                  {calculateTotal().brutto < 250 ? (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      (Optional bei Kleinbetrag)
                    </span>
                  ) : (
                    <span className="text-sm font-normal text-red-500 ml-2">
                      (Vollständige Angaben erforderlich)
                    </span>
                  )}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 relative">
                    <Label htmlFor="customer_name">Kundenname / Firma</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => {
                        setFormData({...formData, customer_name: e.target.value});
                        setShowContactSuggestions(true);
                      }}
                      onFocus={() => setShowContactSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowContactSuggestions(false), 200)}
                      placeholder="Name eingeben oder aus Kontakten wählen"
                      autoComplete="off"
                    />
                    {showContactSuggestions && formData.customer_name && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                        {contacts
                          .filter(contact => 
                            contact.name && 
                            contact.name.toLowerCase().includes(formData.customer_name.toLowerCase())
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .slice(0, 10)
                          .map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex justify-between items-center"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setFormData({...formData, customer_name: contact.name});
                                setShowContactSuggestions(false);
                              }}
                            >
                              <span>{contact.name}</span>
                              <span className="text-xs text-gray-500">{contact.typ}</span>
                            </button>
                          ))}
                        {contacts.filter(c => c.name && c.name.toLowerCase().includes(formData.customer_name.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            Kein Kontakt gefunden - Name wird neu erstellt
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {calculateTotal().brutto >= 250 && (
                    <>
                      <div className="md:col-span-2">
                        <Label htmlFor="customer_street">Straße und Hausnummer *</Label>
                        <Input
                          id="customer_street"
                          value={formData.customer_street}
                          onChange={(e) => setFormData({...formData, customer_street: e.target.value})}
                          required
                          placeholder="Musterstraße 123"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customer_postal_code">PLZ *</Label>
                        <Input
                          id="customer_postal_code"
                          value={formData.customer_postal_code}
                          onChange={(e) => setFormData({...formData, customer_postal_code: e.target.value})}
                          required
                          placeholder="12345"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customer_city">Ort *</Label>
                        <Input
                          id="customer_city"
                          value={formData.customer_city}
                          onChange={(e) => setFormData({...formData, customer_city: e.target.value})}
                          required
                          placeholder="Musterstadt"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label htmlFor="date">Rechnungsdatum *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-slate-700 mb-3">Artikel hinzufügen</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-8 gap-4 mb-4 items-end">
                  <div>
                    <Label>Artikel (nur Bestand)</Label>
                    <Select onValueChange={handleProductSelect} value={currentItem.article}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {products
                          .filter(product => {
                            const stockItem = stock.find(s => s.article === product.name);
                            return stockItem && stockItem.stock > 0;
                          })
                          .map((product) => {
                            const stockItem = stock.find(s => s.article === product.name);
                            return (
                              <SelectItem key={product.id} value={product.name}>
                                {product.name} (Bestand: {stockItem?.stock || 0})
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Label>Preiskategorie</Label>
                    <Select 
                      onValueChange={(value) => setCurrentItem({
                        ...currentItem,
                        price_category: value,
                        markup_percent: markupForCategory(value, "15"),
                      })}
                      value={currentItem.price_category}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kunde">Kunde</SelectItem>
                        <SelectItem value="tp">TP</SelectItem>
                        <SelectItem value="ba">BA</SelectItem>
                        <SelectItem value="five_plus_one">5+1</SelectItem>
                        <SelectItem value="ap">AP</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Label>Menge</Label>
                    <Input
                      type="number"
                      min="1"
                      max={currentItem.article ? (() => {
                        const stockItem = stock.find(s => s.article === currentItem.article);
                        const alreadyUsed = formData.items
                          .filter(item => item.article === currentItem.article)
                          .reduce((sum, item) => sum + item.quantity, 0);
                        return Math.max(1, (stockItem?.stock || 0) - alreadyUsed);
                      })() : undefined}
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({...currentItem, quantity: e.target.value})}
                      placeholder="1"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      {currentItem.article ? (() => {
                        const stockItem = stock.find(s => s.article === currentItem.article);
                        const alreadyUsed = formData.items
                          .filter(item => item.article === currentItem.article)
                          .reduce((sum, item) => sum + item.quantity, 0);
                        const available = (stockItem?.stock || 0) - alreadyUsed;
                        return `Noch verfügbar: ${available} (von ${stockItem?.stock || 0})`;
                      })() : 'Noch verfügbar: - (von -)'}
                    </div>
                  </div>
                  <div>
                    <Label>Aufschlag %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.markup_percent}
                      onChange={(e) => setCurrentItem({...currentItem, markup_percent: e.target.value})}
                      placeholder="15"
                    />
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Label>USt. %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.ust_percent}
                      onChange={(e) => setCurrentItem({...currentItem, ust_percent: e.target.value})}
                      placeholder="19"
                    />
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Label>Preis (Brutto)</Label>
                    <div className="mt-2 text-lg font-semibold text-emerald-600">
                      {currentItem.article ? `${calculatePriceBrutto().toFixed(2)} €` : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Label>Gesamtpreis (Brutto)</Label>
                    <div className="mt-2 text-lg font-bold text-blue-600">
                      {currentItem.article && currentItem.quantity ? 
                        `${(calculatePriceBrutto() * parseInt(currentItem.quantity || 1)).toFixed(2)} €` 
                        : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 invisible">Platzhalter</div>
                  </div>
                  <div>
                    <Button type="button" onClick={addItem} className="w-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Hinzufügen
                    </Button>
                  </div>
                </div>

                {formData.items.length > 0 && (
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <h5 className="font-semibold mb-3">Hinzugefügte Artikel</h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-slate-200">
                            <th className="text-left py-2 px-2">Artikel</th>
                            <th className="text-center py-2 px-2">Menge</th>
                            <th className="text-right py-2 px-2">Einzelpreis</th>
                            <th className="text-center py-2 px-2">USt%</th>
                            <th className="text-right py-2 px-2">Steuerbetrag</th>
                            <th className="text-right py-2 px-2">Gesamtbetrag</th>
                            <th className="text-right py-2 px-2">Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index} className="border-b border-slate-100">
                              <td className="py-2 px-2 font-medium">{item.article}</td>
                              <td className="py-2 px-2 text-center">{item.quantity}</td>
                              <td className="py-2 px-2 text-right">{item.price_brutto?.toFixed(2)} €</td>
                              <td className="py-2 px-2 text-center">{item.ust_percent}%</td>
                              <td className="py-2 px-2 text-right">{item.item_ust?.toFixed(2)} €</td>
                              <td className="py-2 px-2 text-right font-semibold">{item.item_brutto_total?.toFixed(2)} €</td>
                              <td className="py-2 px-2 text-right">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeItem(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 pt-4 border-t-2 border-slate-300">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">Summe Netto:</span>
                          <span className="font-semibold">{calculateTotal().netto.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">Summe USt:</span>
                          <span className="font-semibold">{calculateTotal().ust.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                          <span className="text-lg font-bold">Gesamtsumme (Brutto):</span>
                          <span className="text-2xl font-bold text-emerald-600">
                            {calculateTotal().brutto.toFixed(2)} €
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 mt-2">
                        {calculateTotal().brutto < 250 ? "Kleinbetragsrechnung (Kundendaten optional)" : "Standard-Rechnung (Kundendaten erforderlich)"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Zahlung & Abschluss */}
              <div className="mb-6 p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-3">Zahlung & Abschluss</h4>

                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="bank-details-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeBankDetails}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIncludeBankDetails(checked);
                      if (checked && !dueDate) {
                        // Default: Rechnungsdatum + 14 Tage
                        const base = formData.date ? new Date(formData.date) : new Date();
                        base.setDate(base.getDate() + 14);
                        setDueDate(base.toISOString().split('T')[0]);
                      }
                    }}
                    className="mt-1 w-4 h-4 accent-emerald-600"
                    data-testid="bank-details-checkbox"
                  />
                  <span className="text-sm text-slate-700">
                    <span className="font-medium">Bankdaten + Überweisungssatz auf die Rechnung drucken</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Wenn aktiviert, wird die Rechnung automatisch als Schuld (pro Artikel) erfasst.
                    </span>
                  </span>
                </label>

                {includeBankDetails && (
                  <div className="mt-4 pl-7 space-y-3">
                    <div>
                      <Label htmlFor="due_date">Fälligkeitsdatum *</Label>
                      <div className="flex gap-2 flex-wrap mb-2 mt-1">
                        {[7, 14, 30, 60].map(days => (
                          <button
                            key={days}
                            type="button"
                            data-testid={`due-quick-${days}`}
                            onClick={() => {
                              const base = formData.date ? new Date(formData.date) : new Date();
                              base.setDate(base.getDate() + days);
                              setDueDate(base.toISOString().split('T')[0]);
                            }}
                            className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md font-medium"
                          >
                            +{days} Tage
                          </button>
                        ))}
                      </div>
                      <Input
                        id="due_date"
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        required={includeBankDetails}
                        min={formData.date}
                        data-testid="due-date-input"
                      />
                    </div>

                    {(!companySettings.iban && !companySettings.bank_name) && (
                      <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3 text-sm text-red-800 flex items-start gap-2" data-testid="bank-missing-warning">
                        <span className="text-red-500 text-lg leading-none">!</span>
                        <div className="flex-1">
                          <p className="font-semibold">Keine Bankdaten hinterlegt</p>
                          <p className="text-xs text-red-700 mt-0.5">
                            Um Bankdaten auf die Rechnung zu drucken, bitte IBAN und Bankname in den Einstellungen eintragen.
                          </p>
                          <Link
                            to="/settings"
                            className="inline-block text-xs text-red-700 underline font-semibold mt-1 hover:text-red-900"
                            data-testid="bank-missing-settings-link"
                          >
                            Zu den Einstellungen →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-5">
                  <Label htmlFor="closing_note">Abschlusssatz auf der Rechnung</Label>
                  <Textarea
                    id="closing_note"
                    rows={2}
                    value={closingNote}
                    onChange={(e) => setClosingNote(e.target.value)}
                    placeholder={DEFAULT_CLOSING_NOTE}
                    data-testid="closing-note-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Wird am Ende von PDF und DOCX in Kursiv eingefügt. Leer lassen für Standardtext.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={formData.items.length === 0}
                >
                  Rechnung erstellen
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setFormData({
                      date: new Date().toISOString().split('T')[0],
                      customer_name: "",
                      customer_street: "",
                      customer_postal_code: "",
                      customer_city: "",
                      items: []
                    });
                    setIncludeBankDetails(false);
                    setDueDate("");
                    setClosingNote(DEFAULT_CLOSING_NOTE);
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Rechnungsnr.</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Datum</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Kunde</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Artikel</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Betrag (Brutto)</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Typ</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {groupInvoicesByQuarter(invoices).map((g) => (
                  <Fragment key={g.key}>
                    <tr className="bg-slate-100 border-t-2 border-slate-300" data-testid={`quarter-header-${g.key}`}>
                      <td colSpan={7} className="py-2 px-4">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-700">
                            Q{g.quarter} {g.year}
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              · {g.items.length} Rechnung{g.items.length === 1 ? "" : "en"}
                            </span>
                          </span>
                          <span className="text-sm font-semibold text-emerald-700">
                            Σ {g.total.toFixed(2)} €
                          </span>
                        </div>
                      </td>
                    </tr>
                    {g.items.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium text-emerald-600">{invoice.invoice_number}</td>
                        <td className="py-3 px-4">{new Date(invoice.date).toLocaleDateString('de-DE')}</td>
                        <td className="py-3 px-4">{invoice.customer_name}</td>
                        <td className="py-3 px-4 text-right">{invoice.items.length}</td>
                        <td className="py-3 px-4 text-right font-semibold">{invoice.total_brutto.toFixed(2)} €</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            invoice.invoice_type === 'klein' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {invoice.invoice_type === 'klein' ? 'Klein' : 'Standard'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-2 justify-end">
                            {(() => {
                              const alreadyAsSchuld = schuldenInvoiceNumbers.has(invoice.invoice_number);
                              return (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => !alreadyAsSchuld && addAsSchuld(invoice)}
                                  disabled={alreadyAsSchuld}
                                  className={alreadyAsSchuld
                                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60"
                                    : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300"}
                                  title={alreadyAsSchuld ? "Diese Rechnung ist bereits als Schuld erfasst" : "Als Schulden hinzufügen"}
                                  data-testid={`invoice-add-schuld-${invoice.invoice_number}`}
                                >
                                  {alreadyAsSchuld ? "✓" : "💰"}
                                </Button>
                              );
                            })()}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => requestDownload(invoice.id, invoice.invoice_number)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => deleteInvoice(invoice.id, invoice.invoice_number)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {invoices.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch keine Rechnungen erstellt
              </div>
            )}
          </div>
        </div>

        <InfoModal 
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          category="rechnung"
        />

        <DownloadWarningModal 
          isOpen={showDownloadWarning}
          onClose={() => {
            setShowDownloadWarning(false);
            setPendingDownload(null);
          }}
          onConfirm={confirmDownload}
          title="Rechnung herunterladen"
          type="invoice"
        />

        {/* Format-Wahl-Modal (DOCX / PDF) */}
        {showFormatModal && formatModalInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="format-wahl-modal">
              <div className="bg-gradient-to-br from-slate-700 to-slate-900 px-6 py-4 text-white">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Download className="w-5 h-5" /> Format wählen
                </h3>
                <p className="text-slate-200 text-xs mt-1">Rechnung {formatModalInvoice.invoice_number}</p>
              </div>
              <div className="p-5 space-y-3">
                <button
                  type="button"
                  data-testid="format-docx-btn"
                  onClick={() => {
                    downloadAsDocx(formatModalInvoice);
                    setShowFormatModal(false);
                    setFormatModalInvoice(null);
                  }}
                  className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left flex items-center gap-3 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">DOCX (Word)</p>
                    <p className="text-xs text-slate-500">Editierbar — für Nachbearbeitung in Word</p>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="format-pdf-btn"
                  onClick={() => {
                    downloadAsPdf(formatModalInvoice);
                    setShowFormatModal(false);
                    setFormatModalInvoice(null);
                  }}
                  className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-red-500 hover:bg-red-50 text-left flex items-center gap-3 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">PDF</p>
                    <p className="text-xs text-slate-500">Fertig zum Versenden</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowFormatModal(false);
                    setFormatModalInvoice(null);
                  }}
                  className="w-full py-2 text-slate-500 hover:text-slate-700 text-sm"
                  data-testid="format-cancel-btn"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post-Create-Modal (nach neuer Rechnung) */}
        {showPostCreateModal && postCreateInvoice && (() => {
          const hasSeenSchuldenTip = userStorage.getItem("schuldenPdfTipSeen") === "1";
          return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="post-create-modal">
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-5 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-emerald-50 text-xs font-medium uppercase tracking-wider">Rechnung erstellt</p>
                      <h3 className="text-white text-xl font-bold">{postCreateInvoice.invoice_number}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
                    <p><strong>Kunde:</strong> {postCreateInvoice.customer_name || "Kleinbetragsrechnung"}</p>
                    <p><strong>Betrag:</strong> {postCreateInvoice.total_brutto?.toFixed(2)} €</p>
                    <p><strong>{postCreateInvoice.items?.length || 0} Position(en)</strong></p>
                  </div>

                  {/* Download-Sektion */}
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Jetzt herunterladen:</p>
                    {(() => {
                      const posten = invoiceToErspanisPosten(postCreateInvoice);
                      const showErsparnis = !!postCreateInvoice.include_bank_details;
                      const canErsparnis = showErsparnis && hasErspanis(posten);
                      return (
                        <div className={`grid ${showErsparnis ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
                          <button
                            type="button"
                            data-testid="post-create-docx"
                            onClick={() => downloadAsDocx(postCreateInvoice)}
                            className="p-3 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-center transition-colors"
                          >
                            <FileText className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                            <p className="text-xs font-semibold text-slate-800">Rechnung DOCX</p>
                          </button>
                          <button
                            type="button"
                            data-testid="post-create-pdf"
                            onClick={() => downloadAsPdf(postCreateInvoice)}
                            className="p-3 border-2 border-slate-200 rounded-lg hover:border-red-500 hover:bg-red-50 text-center transition-colors"
                          >
                            <FileText className="w-5 h-5 mx-auto text-red-600 mb-1" />
                            <p className="text-xs font-semibold text-slate-800">Rechnung PDF</p>
                          </button>
                          {showErsparnis && (
                            <button
                              type="button"
                              data-testid="post-create-ersparnis-pdf"
                              disabled={!canErsparnis}
                              onClick={() => downloadErsparnisFromInvoice(postCreateInvoice)}
                              className={`p-3 border-2 rounded-lg text-center transition-colors ${
                                canErsparnis
                                  ? "border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50"
                                  : "border-slate-200 opacity-50 cursor-not-allowed"
                              }`}
                              title={canErsparnis
                                ? "Persönliches Ersparnis-PDF für den Kunden"
                                : "Keine Ersparnis möglich (keine Kundenpreise verfügbar)"}
                            >
                              <Sparkles className={`w-5 h-5 mx-auto mb-1 ${canErsparnis ? "text-emerald-600" : "text-slate-400"}`} />
                              <p className="text-xs font-semibold text-slate-800">Ersparnis PDF</p>
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Als Schuld: abhängig von include_bank_details */}
                  {postCreateInvoice.include_bank_details ? (
                    <div className="border-t border-slate-200 pt-4">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2" data-testid="post-create-auto-schuld-info">
                        <span className="text-emerald-600 text-lg leading-none">✓</span>
                        <div>
                          <p className="font-semibold">Als Schuld erfasst</p>
                          <p className="text-xs text-emerald-700 mt-0.5">
                            Pro Artikel wurde eine Schuld-Position unter Rechnungsnr. {postCreateInvoice.invoice_number} angelegt.
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="post-create-close"
                        onClick={() => {
                          setShowPostCreateModal(false);
                          setPostCreateInvoice(null);
                        }}
                        className="w-full mt-3"
                      >
                        Schließen
                      </Button>
                    </div>
                  ) : (
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Als Schuld erfassen?</p>
                      {!hasSeenSchuldenTip && (
                        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-2 mb-3 text-xs text-amber-800">
                          💡 <strong>Tipp:</strong> Als Schuld erfasst kannst du später eine Ersparnis-Übersicht pro Kunde bauen und die Zahlung einzeln nachverfolgen.
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="post-create-skip-schuld"
                          onClick={() => {
                            if (!hasSeenSchuldenTip) userStorage.setItem("schuldenPdfTipSeen", "1");
                            setShowPostCreateModal(false);
                            setPostCreateInvoice(null);
                          }}
                          className="flex-1"
                        >
                          Nein, schließen
                        </Button>
                        <Button
                          type="button"
                          data-testid="post-create-as-schuld"
                          onClick={() => {
                            if (!hasSeenSchuldenTip) userStorage.setItem("schuldenPdfTipSeen", "1");
                            setShowPostCreateModal(false);
                            const inv = postCreateInvoice;
                            setPostCreateInvoice(null);
                            addAsSchuld(inv);
                          }}
                          className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold"
                        >
                          💰 Als Schuld
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}